import { useState } from 'react';

/**
 * Graph Error Details Component
 * Displays detailed error information for Microsoft Graph API connection issues
 * Matches the pattern of SmtpErrorDetails component
 */
export default function GraphErrorDetails({ error }) {
  const [showTechnicalDetails, setShowTechnicalDetails] = useState(false);

  if (!error) return null;

  // Get error type icon and color
  const getErrorStyle = () => {
    switch (error.type) {
      case 'authentication_failed':
        return {
          color: 'red',
          icon: '🔐',
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200',
          textColor: 'text-red-800',
          badgeColor: 'bg-red-100 text-red-800'
        };
      case 'permission_denied':
        return {
          color: 'orange',
          icon: '🚫',
          bgColor: 'bg-orange-50',
          borderColor: 'border-orange-200',
          textColor: 'text-orange-800',
          badgeColor: 'bg-orange-100 text-orange-800'
        };
      case 'user_not_found':
        return {
          color: 'yellow',
          icon: '👤',
          bgColor: 'bg-yellow-50',
          borderColor: 'border-yellow-200',
          textColor: 'text-yellow-800',
          badgeColor: 'bg-yellow-100 text-yellow-800'
        };
      case 'token_error':
        return {
          color: 'purple',
          icon: '🔑',
          bgColor: 'bg-purple-50',
          borderColor: 'border-purple-200',
          textColor: 'text-purple-800',
          badgeColor: 'bg-purple-100 text-purple-800'
        };
      case 'network_error':
        return {
          color: 'blue',
          icon: '🌐',
          bgColor: 'bg-blue-50',
          borderColor: 'border-blue-200',
          textColor: 'text-blue-800',
          badgeColor: 'bg-blue-100 text-blue-800'
        };
      case 'configuration_invalid':
        return {
          color: 'gray',
          icon: '⚙️',
          bgColor: 'bg-gray-50',
          borderColor: 'border-gray-200',
          textColor: 'text-gray-800',
          badgeColor: 'bg-gray-100 text-gray-800'
        };
      default:
        return {
          color: 'red',
          icon: '❌',
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200',
          textColor: 'text-red-800',
          badgeColor: 'bg-red-100 text-red-800'
        };
    }
  };

  const style = getErrorStyle();

  return (
    <div className={`rounded-lg border ${style.borderColor} ${style.bgColor} p-4 space-y-4`}>
      {/* Header */}
      <div className="flex items-start">
        <div className="flex-shrink-0 text-2xl">
          {style.icon}
        </div>
        <div className="ml-3 flex-1">
          <h3 className={`text-sm font-medium ${style.textColor}`}>
            {error.message}
          </h3>
          <div className="mt-1">
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${style.badgeColor}`}>
              {error.type?.replace(/_/g, ' ').toUpperCase() || 'ERROR'}
            </span>
          </div>
        </div>
      </div>

      {/* Error Details */}
      {error.details && (
        <div className={`text-sm ${style.textColor} pl-10`}>
          <p>{error.details}</p>
        </div>
      )}

      {/* Suggestions */}
      {error.suggestions && error.suggestions.length > 0 && (
        <div className="pl-10">
          <h4 className={`text-sm font-medium ${style.textColor} mb-2`}>
            💡 Suggestions to fix this:
          </h4>
          <ul className={`list-disc list-inside space-y-1 text-sm ${style.textColor}`}>
            {error.suggestions.map((suggestion, index) => (
              <li key={index} className="ml-2">
                {suggestion}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Technical Details (Expandable) */}
      {error.technicalDetails && (
        <div className="pl-10">
          <button
            onClick={() => setShowTechnicalDetails(!showTechnicalDetails)}
            className={`text-sm font-medium ${style.textColor} hover:underline focus:outline-none flex items-center gap-1`}
          >
            <span>{showTechnicalDetails ? '▼' : '▶'}</span>
            Technical Details
          </button>
          
          {showTechnicalDetails && (
            <div className={`mt-2 p-3 bg-white rounded border ${style.borderColor}`}>
              <dl className="space-y-2 text-xs">
                {Object.entries(error.technicalDetails).map(([key, value]) => (
                  <div key={key} className="flex">
                    <dt className="font-semibold text-gray-700 w-32 flex-shrink-0">
                      {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:
                    </dt>
                    <dd className="text-gray-600 break-all">
                      {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          )}
        </div>
      )}
    </div>
  );
}